// Scope: listens to all joined rooms and renders the currently selected room workspace.

import React, { useCallback, useEffect, useRef, useState } from "react";
import ScrollToBottom from "react-scroll-to-bottom";
import useChatRoomStore from "../../chatRoomStore";

const formatMessageTime = (value) => new Date(value || Date.now()).toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
});

const GroupChat = ({ socket, chatId, visible = true, onIncoming, onClose, connectionState, typingText }) => {
    const {
        activeRoom,
        joinedRooms,
        roomMessages,
        appendRoomMessage,
        leaveRoomTab,
        drafts,
        setDraft,
    } = useChatRoomStore();
    const [sending, setSending] = useState(false);
    const typingTimerRef = useRef();
    const workspace = activeRoom ? `room:${activeRoom.id}` : "room:none";
    const messageInput = drafts[workspace] || "";

    const handleIncomingRoomMessage = useCallback((data) => {
        if (!data?.roomId || !joinedRooms.some((room) => room.id === data.roomId)) return;
        appendRoomMessage(data);
        onIncoming?.(data);
    }, [appendRoomMessage, joinedRooms, onIncoming]);

    useEffect(() => {
        if (!socket) return undefined;
        socket.on("receiveRoomMessage", handleIncomingRoomMessage);
        socket.on("roomSystemMessage", handleIncomingRoomMessage);
        return () => {
            socket.off("receiveRoomMessage", handleIncomingRoomMessage);
            socket.off("roomSystemMessage", handleIncomingRoomMessage);
        };
    }, [socket, handleIncomingRoomMessage]);

    if (!activeRoom) return null;

    const visibleMessages = roomMessages.filter((message) => message.roomId === activeRoom.id);

    const handleSendMessage = () => {
        const message = messageInput.trim();
        if (!socket?.connected || !message || sending) return;
        setSending(true);
        socket.emit("sendRoomMessage", { message, roomId: activeRoom.id }, (result) => {
            setSending(false);
            if (result?.ok) setDraft(workspace, "");
        });
    };

    const handleExit = () => {
        socket?.emit("leaveRoom", activeRoom.id);
        leaveRoomTab(activeRoom.id);
        onClose?.(activeRoom.id);
    };

    return (
        <div className={`active-chat-container conversation-panel${visible ? "" : " is-hidden"}`}>
            <div className="chat-header">
                <div className="chat-title">
                    <h2># {activeRoom.name}</h2>
                    <span className="chat-header__type">{activeRoom.isPrivate ? "Private room" : "Open room"}</span>
                </div>
                <button onClick={handleExit} className="exit-btn">Leave room</button>
            </div>

            {connectionState !== "connected" && (
                <div className="conversation-connection-state" role="status">
                    Reconnecting… Your room tabs and drafts are preserved.
                </div>
            )}

            <ScrollToBottom className="chat-messages" mode="bottom" behavior="smooth">
                <div>
                    {visibleMessages.map((message, index) => {
                        const previous = visibleMessages[index - 1];
                        const isSystem = message.type === "system";
                        const isMine = !isSystem && String(message.sender?.id) === String(chatId);
                        const showAuthor = !isSystem && (!previous || previous.type === "system"
                            || String(previous.sender?.id) !== String(message.sender?.id));
                        return (
                            <div key={message.id || message.timeStamp || index} className={`group-messages ${isSystem ? "system" : isMine ? "mine" : "others"}`}>
                                {isSystem ? <div className="system-message">{message.text}</div> : (
                                    <>
                                        <div className="message-meta">
                                            {showAuthor && <span className="message-author">{isMine ? "You" : message.sender?.name}</span>}
                                            <time>{formatMessageTime(message.timeStamp)}</time>
                                        </div>
                                        <div className={isMine ? "my-message" : "other-message"}>{message.message}</div>
                                    </>
                                )}
                            </div>
                        );
                    })}
                </div>
            </ScrollToBottom>
            <div className="typing-indicator" aria-live="polite">{typingText || "\u00a0"}</div>

            <div className="chat-input">
                <textarea
                    placeholder={`Message #${activeRoom.name}`}
                    rows="2"
                    value={messageInput}
                    maxLength={1000}
                    aria-label={`Message ${activeRoom.name}`}
                    onChange={(event) => {
                        setDraft(workspace, event.target.value);
                        socket?.emit("roomTyping", { roomId: activeRoom.id, isTyping: true });
                        window.clearTimeout(typingTimerRef.current);
                        typingTimerRef.current = window.setTimeout(() => {
                            socket?.emit("roomTyping", { roomId: activeRoom.id, isTyping: false });
                        }, 1_200);
                    }}
                    onKeyDown={(event) => {
                        if (event.key === "Enter" && !event.shiftKey) {
                            event.preventDefault();
                            handleSendMessage();
                        }
                    }}
                />
                <div className="chat-compose-actions">
                    <span className="chat-character-count">{messageInput.length}/1000</span>
                    <button className="send-btn" onClick={handleSendMessage} disabled={connectionState !== "connected" || sending || !messageInput.trim()}>
                        {sending ? "Sending…" : "Send"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default GroupChat;
